import React, { useState, useEffect } from 'react';
import {
  CapabilityDecision,
  CAPABILITY_DOMAIN_LABELS,
} from './types';
import { getLatestAssessment } from './capabilityStorage.service';

interface CapabilityOverviewProps {
  goalId: string;
  ownerId: string;
}

/**
 * CapabilityOverview - Permanent display of skill assessment
 * 
 * Shows the latest capability assessment as a read-only bar chart.
 * Only displayed if an assessment exists.
 */
const CapabilityOverview: React.FC<CapabilityOverviewProps> = ({
  goalId,
  ownerId,
}) => {
  const [decisions, setDecisions] = useState<CapabilityDecision[]>([]);
  const [timestamp, setTimestamp] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAssessment = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const assessment = await getLatestAssessment(ownerId, goalId);
        
        if (assessment) {
          setDecisions(assessment.decisions);
          setTimestamp(assessment.completedAt);
        }
      } catch (err: any) {
        console.error('Failed to load capability assessment:', err);
        setError('Kan capaciteiten niet laden.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAssessment();
  }, [ownerId, goalId]);

  // Helper: Get skill bar configuration per decision
  const getSkillBarConfig = (decision: CapabilityDecision) => {
    if (!decision.required) {
      return { type: 'badge' as const, text: 'Niet van toepassing', color: 'gray' };
    }
    
    if (decision.responsibility === 'other') {
      return { type: 'badge' as const, text: 'Wordt georganiseerd', color: 'blue' };
    }
    
    if (decision.responsibility === 'self') {
      const levelMap = {
        'basic': { width: 33, label: 'Basis' },
        'workable': { width: 67, label: 'Werkbaar' },
        'advanced': { width: 100, label: 'Gevorderd' },
      };
      
      const config = levelMap[decision.level!];
      const action = decision.resolution 
        ? ` → ${decision.resolution === 'learn' ? 'Leren' 
            : decision.resolution === 'outsource' ? 'Uitbesteden' 
            : 'Aannemen'}`
        : '';
      
      return { 
        type: 'bar' as const, 
        width: config.width, 
        label: `${config.label} (zelf)${action}`,
      };
    }
    
    return { type: 'badge' as const, text: 'Onbekend', color: 'gray' };
  };

  // Format timestamp
  const formatDate = (ts: number) => {
    const date = new Date(ts);
    return date.toLocaleDateString('nl-NL', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-sm text-gray-500">Laden...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (decisions.length === 0) {
    return null; // Don't show if no assessment
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Je capaciteiten overzicht
        </h3>
        {timestamp && (
          <span className="text-xs text-gray-500">
            Laatst gecontroleerd: {formatDate(timestamp)}
          </span>
        )}
      </div>

      {/* Bar chart */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        {decisions.map((decision) => {
          const config = getSkillBarConfig(decision);
          const domain = CAPABILITY_DOMAIN_LABELS[decision.domain];
          
          return (
            <div key={decision.domain} className="space-y-1.5">
              <div className="text-sm font-medium text-gray-700">{domain}</div>
              
              {config.type === 'bar' ? (
                <div className="flex items-center space-x-3">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gray-300 transition-all"
                      style={{ width: `${config.width}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-600 w-52 flex-shrink-0">{config.label}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full" />
                  <span className="text-xs w-52 flex-shrink-0">
                    <span className={`px-2 py-0.5 rounded ${
                      config.color === 'blue' 
                        ? 'bg-blue-50 border border-blue-200 text-blue-700'
                        : 'bg-gray-100 border border-gray-200 text-gray-600'
                    }`}>
                      {config.text}
                    </span>
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CapabilityOverview;
